/**
 * The UM's half of the Report Studio — where the owning Umpires Manager writes the narrative,
 * moves the report down the pipeline and presses W3.
 *
 * Authority: `um-app/06-report-studio.md` §4.1 (the draft box and its 800-character meter),
 * §4.3a (the read-only `TD Closing Remarks` block), §4.4 (the actions and the W3 rules), §4.5
 * (the pre-push strip) and §6 (the states); `05-reporting-marking.md` §A5, §B6 and §B9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A COMPONENT OF ITS OWN, AND WHAT IT REFUSES TO FORK
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `features/marking/ReportComposer.tsx` is the **Technical Delegate's** composer, and on an
 * umpire its whole shape is the inverse of this one: there the closing remarks are the box and
 * the narrative is the read-only context beneath; here the narrative is the box and the
 * remarks are the read-only block above. One component doing both would be an ownership
 * conditional wrapped round every label, control and hint on the screen.
 *
 * **What is shared is everything that could otherwise disagree**, and it is imported rather
 * than restated: `assembleReportText` (ruling §E's serialisation), `buildReportPayload`, the
 * stage map (`legalReportStageTransitions`, `REPORT_STAGE_LABELS`, `REPORT_STAGE_TOOLTIPS`,
 * `reportStageActionLabel`, `stageRequiresReportText`), the §B6 character contract (`LIMIT`,
 * `NEAR`, `counterClass`, `counterTitle` — the 800 is one number in one place) and the three
 * push components `PushConfirm`, `PushFallbacks` and `PushReceiptNote`. Not one line of
 * arithmetic or serialisation is reimplemented here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HONESTY RULES, CARRIED OVER UNCHANGED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **The stage pill is server-confirmed, always.** No transition sets it before the reply
 *     lands. A press in flight disables the control; a refusal leaves the pill untouched.
 *  2. **The D-4 markers are gated on the SAVED report**, never on the textarea — the route
 *     weighs what is stored, so text still in the box gets "save it first" rather than a
 *     button that leads to a 422 contradicting the words on screen.
 *  3. **The meter counts the assembled text**, TD block included, because that is what goes
 *     into the one TMS field (§4.3a: *"It counts towards the 800-character meter"*).
 *  4. **A push is a moment, not a state.** `Edited Since Last Push` is the server's own
 *     comparison of the accepted snapshot with the text as it now stands.
 *  5. **Nothing is automatic.** No autosave, no retry, no chained save-then-push: one press,
 *     one act, and the dialogue says so where the box has moved on from the stored report.
 *
 * **The push is ONE press for the marks AND the report** (§4.5, ruled by Alfred for run 11).
 * The composer mints the press id, hands it to `pushFinalReport` and renders whichever of the
 * three answers came back — both parts in TMS, one part in TMS, or neither — through
 * `FinalPushReceipt`, which draws both halves on every outcome. A press made offline is
 * queued as one unit and rendered as queued, with no TMS outcome claimed for it.
 *
 * **What is deliberately NOT here.** The AI draft (`Re-Draft From Notes`), `Download Word` on
 * the TD letterhead, the evidence pane, the `Also Waiting` queue's live note counts and the
 * autosave of §4.7 — each needs a slice this one does not contain (the drafting call, the
 * document generator, the notes-per-umpire projection, the offline queue). They are absent
 * rather than stubbed: an autosave chip reading `Autosaved` over a save that never happened is
 * exactly the failure §4.7 exists to forbid. `Copy For TMS` ships, through `PushFallbacks`,
 * because it needs nothing but the payload already on screen.
 */

import { useState } from "react";

import {
  assembleReportText,
  buildReportPayload,
  isReportReleased,
  legalReportStageTransitions,
  REPORT_STAGE_LABELS,
  REPORT_STAGE_TOOLTIPS,
  REPORT_TEXT_REQUIRED_HINT,
  reportStageActionLabel,
  stageRequiresReportText,
  type ReportStage,
  type TmsReportPayload,
} from "@hockeytd/shared";

import {
  ApiError,
  newPressId,
  postUmReportStage,
  pushFinalReport,
  putUmReportText,
} from "../../api/client.js";
import type { WriteOutcome } from "../../api/noteQueue.js";
import type { FinalPushBody, MarkingPanelBody, ReportBody } from "../../api/types.js";
import { panelPayload } from "../marking/MarkingPanel.js";
import { FinalPushReceipt } from "./FinalPushReceipt.js";
import {
  COUNT_CAPTION,
  LIMIT,
  PREVIEW_LABEL,
  counterClass,
  counterTitle,
} from "../marking/ReportComposer.js";
import { PushConfirm } from "../marking/PushConfirm.js";
import { PushFallbacks } from "../marking/PushFallbacks.js";

/** §4.1's register, as the drafting instruction states it — descriptive, no verdicts. */
const NARRATIVE_PLACEHOLDER =
  "Write the report in your own words — what the evidence showed, described plainly. No " +
  "marks, no verdicts, no attribution.";

/** Ruling D-4's other half: text that exists, but only in the box. */
const SAVE_FIRST_HINT = "Save your text first — the marker reads the saved report.";

export type UmReportTextWriter = (
  tmsPersonId: string,
  tdDraftText: string | null,
) => Promise<ReportBody>;

export type UmReportStageWriter = (
  tmsPersonId: string,
  stage: ReportStage,
) => Promise<ReportBody>;

/**
 * THE SINGLE PRESS (um-app/06 §4.5, ruled by Alfred for run 11).
 *
 * One press sends the marks and the report together. It answers a `WriteOutcome`, because a
 * press made with no signal is queued on the phone as one unit and has no server answer to
 * give — and the composer must not be able to render one it was not given.
 */
export type UmFinalPusher = (
  tmsPersonId: string,
  pressId: string,
) => Promise<WriteOutcome<FinalPushBody>>;

export interface UmReportComposerProps {
  /** The umpire the report is about. */
  umpireName: string;
  /**
   * The report as fetched. Held as seed state, which is safe because the screen mounts this
   * component under `key={tmsPersonId}` — a different umpire is a different instance.
   */
  initialReport: ReportBody;
  /**
   * Where the agreed marks stand, for §4.5's pre-push strip. **Advisory only** — *"Every line
   * in this strip is advisory: none of them blocks the push."*
   */
  marks?: { stated: number; total: number; outcome: number | null; pushedAt: string | null };
  /** Injected in tests only; the real writes are the `um` client functions. */
  saveText?: UmReportTextWriter;
  transition?: UmReportStageWriter;
  push?: UmFinalPusher;
  /**
   * The marking panel this press would send, for the confirmation dialogue's second half and
   * for §4.5's advisory line. Absent while it has not loaded — the dialogue then names the
   * report only and says so, rather than itemising marks it has not read.
   */
  panel?: MarkingPanelBody | null;
}

/** The text as it would go into the TMS field — ruling §E, the same assembly the API uses. */
export function umPreviewText(report: ReportBody, narrative: string): string {
  return assembleReportText({
    tdClosingRemarks: report.tdClosingRemarks,
    umBody: narrative,
  });
}

/**
 * What a push would actually send — the report the server has **stored**, so the dialogue, the
 * clipboard text and the receipt all describe the same document.
 */
export function umPushPayload(report: ReportBody, umpireName: string): TmsReportPayload {
  return buildReportPayload({
    subjectTmsPersonId: report.tmsPersonId,
    subjectName: umpireName,
    tournamentId: report.tournamentId,
    text: report.assembledText,
  });
}

export function UmReportComposer({
  umpireName,
  initialReport,
  marks,
  panel = null,
  saveText = putUmReportText,
  transition = postUmReportStage,
  push = pushFinalReport,
}: UmReportComposerProps): React.JSX.Element {
  const [report, setReport] = useState<ReportBody>(initialReport);
  /** What the manager is typing — her narrative, and never the TD's block. */
  const [narrative, setNarrative] = useState<string>(initialReport.tdDraftText ?? "");
  const [busy, setBusy] = useState<boolean>(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);
  /** The single press's answer — both halves — or null before one has been made. */
  const [pushed, setPushed] = useState<FinalPushBody | null>(null);
  /**
   * A press that is durable on the phone and has NOT reached the server. Held separately
   * from `pushed` and rendered separately, because it is a different question: there is no
   * TMS outcome to show, and a screen that put one here would be inventing it.
   */
  const [queuedAt, setQueuedAt] = useState<string | null>(null);

  const released = isReportReleased(report.stage);
  const preview = released ? report.assembledText : umPreviewText(report, narrative);
  const count = preview.length;
  const overLimit = count > LIMIT;

  /**
   * §B9 / ruling D-4, measured on the SAVED report: `assembledText` is the server's own
   * serialisation of what is stored, so a marker is offered only when the server would grant
   * it. The TD's remarks alone count as text — the report does hold something, even before the
   * manager has written her part.
   */
  const holdsText = report.assembledText.trim() !== "";
  const awaitingSave = !holdsText && preview.trim() !== "";
  const unsavedEdits = !released && preview !== report.assembledText;

  /** The push is the manager's own (W3), so no transition is filtered out of her row. */
  const transitions: readonly ReportStage[] = legalReportStageTransitions(report.stage);
  const markersWithheld = !holdsText && transitions.some(stageRequiresReportText);

  const pushPayload = umPushPayload(report, umpireName);
  /** The other half of the press, itemised to the same standard (see `PushConfirm`). */
  const marksHalf = panel === null ? null : panelPayload(panel, umpireName);
  const showEditedChip = report.editedSinceLastPush && !released;

  /** The server's report wins outright — stage, stored text and assembled text alike. */
  function reconcile(authoritative: ReportBody): void {
    setReport(authoritative);
    setNarrative(authoritative.tdDraftText ?? "");
  }

  async function commitText(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      // NOT optimistic. The typed text is already on screen, so a provisional report would buy
      // no immediacy — and it would enable the D-4 markers on the strength of a save that has
      // not landed. The server's reply is the only thing that moves `report`.
      reconcile(await saveText(report.tmsPersonId, narrative === "" ? null : narrative));
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The report was not saved. Your text is still here, unsent.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function commitStage(to: ReportStage): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      reconcile(await transition(report.tmsPersonId, to));
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The report did not move. Nothing was sent to TMS.",
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * **THE SINGLE PRESS** (§4.5, ruled by Alfred for run 11) — the marks and the report, in
   * one act, through one route.
   *
   * Two answers are possible and they are kept apart, because they are different facts:
   *
   *  * `sent: false` — the press is **durable on the phone and has not reached the server**.
   *    There is no TMS outcome and none is rendered; the screen says `Push Queued` and says
   *    what that means. This is the offline path, and the press is queued as ONE unit.
   *  * `sent: true` — the server answered, and the envelope says what TMS did with **each
   *    half**. A `partial` is rendered as a partial: never a single `Sent` over a failed half.
   *
   * Nothing retries. A half TMS refused is re-sent only by a new human press, which mints a
   * new press id — pressing again after a partial sends the outstanding half and returns the
   * stored receipt for the one already in TMS.
   */
  async function commitPush(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      // Minted once, here, per press — not per attempt. It is the identity that survives the
      // queue and makes a replay safe (migration 026).
      const outcome = await push(report.tmsPersonId, newPressId());
      setConfirming(false);

      if (!outcome.sent) {
        setQueuedAt(outcome.queuedAt);
        setPushed(null);
        return;
      }

      setQueuedAt(null);
      setPushed(outcome.receipt);
      // The server's report wins outright — its stage moved only if the report half landed.
      reconcile(outcome.receipt.reportState);
    } catch (error) {
      setConfirming(false);
      // A refusal BEFORE anything was sent — an incomplete marking, an illegal transition.
      // There is no receipt because there was no press, and none is invented.
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The press did not go through. Nothing reached TMS; your marks and your report " +
            "are still here.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="panel panel--d1 umcomposer"
      aria-labelledby="umComposerTitle"
      data-howto="um-composer"
    >
      <div className="panel__head">
        {/*
         * "Your Report", not "AI Draft": no AI function lives in this panel (`Re-Draft From
         * Notes` is deliberately unbuilt — see the header), and the old title read as a
         * TD-side AI tool leaking into the UM app. It misled the TD himself in live testing
         * (2026-09-09), which is the whole case against it: a title must name what the panel
         * is, and this panel is the manager's own narrative over the whole tournament.
         */}
        <h3 className="panel__title" id="umComposerTitle">
          Your Report — Whole Tournament
        </h3>
        <span className="panel__note">Yours To Edit</span>
        <span className="spacer" />
        <span
          className={`statuspill ${released ? "statuspill--good" : "statuspill--warn"}`}
          title={REPORT_STAGE_TOOLTIPS[report.stage]}
          data-testid="um-report-stage"
        >
          <span className="sdot" />
          {REPORT_STAGE_LABELS[report.stage]}
        </span>
        {showEditedChip ? (
          <span
            className="editchip"
            title="This text has been saved here since the last push. TMS still holds the earlier version until it is pushed again."
            data-testid="um-edited-since-push"
          >
            Edited Since Last Push
          </span>
        ) : null}
      </div>

      <div className="airep">
        {/* §4.3a — the Technical Delegate's block, above the narrative and read-only here. */}
        <div className="tdblock" data-testid="td-closing-remarks">
          <span className="composerlabel">Technical Delegate · Closing Remarks</span>
          <pre className="previewbox previewbox--muted">
            {report.tdClosingRemarks ?? "No Closing Remarks Yet"}
          </pre>
          <div className="hidenote">
            <b>The Technical Delegate&rsquo;s words —</b> you see them, they count towards your{" "}
            {LIMIT} characters and they go with your push, but they are not yours to edit.
          </div>
        </div>

        {released ? (
          <div className="hidenote" data-testid="um-released-note">
            <b>Published in TMS —</b> this report is confirmed and can no longer be edited here.
            TMS holds the published record, and the app reads that stage back rather than
            setting it.
          </div>
        ) : null}

        <label className="composerlabel" htmlFor="umComposerText">
          Your report on {umpireName} — it follows the Technical Delegate&rsquo;s block
        </label>
        <textarea
          id="umComposerText"
          className="aitext"
          data-testid="um-composer-text"
          aria-label="Your report narrative"
          placeholder={NARRATIVE_PLACEHOLDER}
          value={narrative}
          readOnly={released}
          onChange={(event) => {
            setNarrative(event.target.value);
          }}
        />

        <div className="aibar">
          {released ? null : (
            <button
              type="button"
              className="gbtn gbtn--solid"
              data-testid="um-save-text"
              disabled={busy}
              onClick={() => void commitText()}
            >
              Save Draft
            </button>
          )}
          <span
            className={counterClass(count)}
            title={counterTitle(count)}
            data-testid="um-charcount"
          >
            {count} / {LIMIT}
          </span>
          <span className="btnhint" data-testid="um-charcount-caption">
            {COUNT_CAPTION}
          </span>
        </div>

        {failure === null ? null : (
          <div className="writeerror" role="alert" data-testid="um-write-error">
            <b>Not Saved</b>
            {failure}
          </div>
        )}

        <div className="composerprev" data-howto="um-preview">
          <span className="composerlabel">{PREVIEW_LABEL}</span>
          <pre className="previewbox" data-testid="um-composer-preview">
            {preview}
          </pre>
          {preview === "" ? (
            <div className="hidenote">
              <b>Blank —</b> nothing has been written yet, by either of you.
            </div>
          ) : null}
        </div>

        {/* §4.5's pre-push strip. Every line is ADVISORY — none of them blocks the push. */}
        <div className="gapstrip" data-testid="um-before-you-push">
          <b>Before You Push</b>
          <div>
            <b>Marks —</b>{" "}
            {marks === undefined
              ? "the agreed set is on the marking screen."
              : marks.pushedAt !== null
                ? `pushed, ${String(marks.stated)} of ${String(marks.total)} categories, outcome ${
                    marks.outcome === null ? "—" : String(marks.outcome)
                  }.`
                : `${String(marks.stated)} of ${String(marks.total)} categories stated${
                    marks.outcome === null ? "" : `, outcome ${String(marks.outcome)}`
                  } — not pushed yet.`}{" "}
            {/* §4.5, as ruled: *"**Marks Included** — the nine agreed category marks and the
                computed outcome go with this report."* One press sends both, and this line
                now says so because it is now true. */}
            <b>Marks Included</b> — the agreed category marks and the computed outcome go with
            this report, in the same press.
          </div>
          <div>
            <b>TD Closing Remarks —</b>{" "}
            {report.tdClosingRemarks === null
              ? "None Written Yet — the push proceeds without them."
              : `Included, ${String(report.tdClosingRemarks.length)} characters.`}
          </div>
          {overLimit ? (
            <div data-testid="um-over-limit">
              <b>{LIMIT} Character Limit Reached —</b> trim {count - LIMIT}. The Technical
              Delegate&rsquo;s block counts towards this total.
            </div>
          ) : null}
        </div>

        <div className="composerbar" data-testid="um-stage-actions">
          {transitions.map((to) => (
            <button
              key={to}
              type="button"
              className={to === 3 ? "gbtn gbtn--solid" : "gbtn"}
              data-testid={`um-stage-action-${to}`}
              disabled={busy || confirming || (stageRequiresReportText(to) && !holdsText)}
              onClick={() => {
                // The push is the one transition that reaches TMS, so it goes through the
                // confirmation dialogue (gate G-5) and through the push route.
                if (to === 3) setConfirming(true);
                else void commitStage(to);
              }}
            >
              {to === 3 && report.stage !== 3
                ? "Push To TMS As Draft"
                : reportStageActionLabel(report.stage, to)}
            </button>
          ))}

          {markersWithheld ? (
            <span className="btnhint" data-testid="um-stage-text-required">
              {awaitingSave ? SAVE_FIRST_HINT : REPORT_TEXT_REQUIRED_HINT}
            </span>
          ) : null}

          {transitions.includes(3) ? (
            <span className="btnhint">Nothing Is Automatic · Pushed as draft.</span>
          ) : null}
        </div>

        {confirming ? (
          <PushConfirm
            payload={pushPayload}
            alsoSending={marksHalf}
            officialName={umpireName}
            confirmLabel={report.stage === 3 ? "Confirm Repush" : "Confirm Push"}
            busy={busy}
            unsavedEdits={unsavedEdits}
            {...(pushPayload.charCount > LIMIT
              ? {
                  blocker:
                    `${pushPayload.charCount} characters — TMS rejects anything over ${LIMIT}. ` +
                    `Trim ${pushPayload.charCount - LIMIT}.`,
                }
              : pushPayload.text.trim() === ""
                ? { blocker: "The report is empty." }
                : {})}
            onConfirm={() => void commitPush()}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        ) : null}

        {/* THE PRESS'S OWN ANSWER. A queued press and a delivered one are different states
            and are rendered as different things — never one shape with a hopeful label. */}
        {queuedAt === null ? null : (
          <div className="writequeued" role="status" data-testid="final-push-queued">
            <b>Push Queued —</b> you are offline, so this press is saved on your phone as one
            unit and will go when you are back. <b>Nothing has reached TMS yet</b>, and no
            receipt exists until it does. The marks and the report go together when it sends.
          </div>
        )}
        {pushed === null ? null : <FinalPushReceipt result={pushed} />}

        {/* §4.4's alternatives ship BESIDE the push, not after it fails, and they render from
            state already held — so they work when TMS cannot be reached at all. */}
        {released ? null : (
          <>
            <PushFallbacks payload={pushPayload} />
            {unsavedEdits ? (
              <span className="btnhint" data-testid="um-fallbacks-unsaved-edits">
                These carry the saved report. Your unsaved edits are not in them — save first to
                include them.
              </span>
            ) : null}
          </>
        )}
      </div>

      <div className="panel__foot">
        <b>Draft · Nothing Sent Until You Push —</b> one report per umpire for the whole
        tournament. The Technical Delegate&rsquo;s block heads the text as{" "}
        <span className="num">TD - </span>, yours follows as <span className="num">UM - </span>,
        and both prefixes are added when the report is put together — never typed, never stored.
      </div>
    </section>
  );
}
